/* eslint-disable react/destructuring-assignment */
import { Fragment, useEffect, useState } from "react";
import Button from "@Components/Button";
import PaymentChevronDown from "@Icons/PaymentChevronDown";
import LoaderIcon from "@Icons/LoaderIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import styles from "../../RoomTypeSection.module.scss";
import useRoomTypeContext from "../../context/useRoomTypeContext";
import { getSoldOutRooms, unitTypes } from "../../utils";
import unitTypesDescription from "../../utils/unitTypesDescription";
import NoRoomTypesFound from "../NoRoomTypesFound";
import RoomTypeCard from "../RoomTypeCard";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import RoomTypeCardV2 from "../RoomTypeCard/RoomTypeCardV2";
import useIsDesktop from "hooks/useIsDesktop";
//

const CustomAccordionItem = (props: any) => {
  const {
    roomTypes: { isLoading = false },
  } = useRoomTypeContext();

  const isRoomTypeV2 = true;
  const [availableRooms1, soldOutRooms1] = getSoldOutRooms(props?.data);
  const [availableRooms, setAvailableRooms] = useState(availableRooms1);
  const [soldOutRooms, setSoldOutRooms] = useState(soldOutRooms1);
  const [rooms, setRooms] = useState(availableRooms);
  const [isSoldOutExpanded, setIsSoldOutExpanded] = useState(false);

  useEffect(() => {
    const [availableRooms2, soldOutRooms2] = getSoldOutRooms(props?.data);
    setAvailableRooms(availableRooms2);
    setSoldOutRooms(soldOutRooms2);
    if (isSoldOutExpanded) {
      setRooms([...availableRooms2, ...soldOutRooms2]);
    } else {
      setRooms(availableRooms2);
    }
  }, [props?.activeTab, props?.data, isLoading]);

  useEffect(() => {
    const { hash } = window.location;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    if (hash === "#room_type") {
      timerId = setTimeout(() => {
        const element = document.getElementById("room_type");
        if (element) {
          const headerOffset = 54 + 48; // padding plus navbar height
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 500);
    } else if (hash.startsWith("#room-")) {
      // Scroll to specific room type card (from Video Page "View Room" button)
      timerId = setTimeout(() => {
        const elementId = hash.replace("#", "");
        const element = document.getElementById(elementId);
        if (element) {
          const headerOffset = 54 + 48;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        } else {
          // Fallback: scroll to room_type section if specific room not found (e.g., sold out)
          const fallbackElement = document.getElementById("room_type");
          if (fallbackElement) {
            const headerOffset = 54 + 48;
            const elementPosition = fallbackElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }
        }
      }, 500);
    }
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  const updateRoomCount = () => {
    if (props.data.length > rooms.length) {
      setRooms([...availableRooms, ...soldOutRooms]);
      setIsSoldOutExpanded(true);
      document
        .getElementById(`room-${availableRooms[availableRooms.length - 1].id}`)
        ?.scrollIntoView({ behavior: "auto", block: "start", inline: "nearest" });
      gtagEventEmitter({
        action: "more_room_loaded",
        category: PAGE.INVENTORY,
        label: "success",
      });
    } else {
      setRooms([...availableRooms]);
      setIsSoldOutExpanded(false);
      document
        .getElementById(`room-${availableRooms[availableRooms.length - 1].id}`)
        ?.scrollIntoView({ behavior: "auto", block: "start", inline: "nearest" });
      gtagEventEmitter({
        action: "more_room_collapsed",
        category: PAGE.INVENTORY,
        label: "success",
      });
    }
  };

  const [isDescriptionOpen, setisDescriptionOpen] = useState(true);

  const handleTitleClick = () => {
    gtagEventEmitter({
      action: isDescriptionOpen ? "room_type_collapsed" : "room_type_expanded",
      category: PAGE.INVENTORY,
      tab: props?.tab,
    });
    setisDescriptionOpen(!isDescriptionOpen);
  };

  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  const unitTypesDesc: any = unitTypesDescription(t);

  return (
    <div
      id={unitTypes[props?.tab]}
      className={`${props.className} ${
        isDescriptionOpen ? "accordion-item active" : "accordion-item"
      } ${formatClassNames(styles.accordionItem, isDesktop && styles.desktop)}`}
      key={props.index}
    >
      {isDesktop && (
        <div
          className={formatClassNames(
            styles.accordionHeader,
            props.isDesktop && styles.accordionHeader_desktop,
            isRoomTypeV2 && styles.headerv2,
            isDescriptionOpen && styles.active,
          )}
          onClick={handleTitleClick}
          onKeyDown={handleTitleClick}
          role="none"
          id={`room-type-card-container-${unitTypes[props?.tab]}`}
        >
          <div className={styles.iconContainer}>{unitTypesDesc[props?.tab]?.icon}</div>
          <div className={styles.accordianItemTitleSection}>
            <h3
              data-testid="property-room-type-unit-title"
              className={formatClassNames(styles.accordianItemTitle, isDesktop && styles.desktop)}
            >
              {unitTypes[props?.tab]}
            </h3>
            <div
              data-testid="property-room-type-unit-subtitle"
              className={formatClassNames(
                styles.accordianItemSubTitle,
                props.isDesktop && styles.accordianItemSubTitle_desktop,
              )}
            >
              {t(unitTypesDesc[props?.tab]?.text)}
            </div>
          </div>
          <div className={isDescriptionOpen ? "chevron active" : "chevron"}>
            <PaymentChevronDown width={12} height={12} />
          </div>
        </div>
      )}
      <div
        className={formatClassNames(
          "accordion-content",
          styles.accordionContent,
          props.isDesktop && styles.noPaddingBottom,
        )}
      >
        {isLoading ? (
          <div className={styles.loader}>
            <LoaderIcon />
          </div>
        ) : (
          <>
            {rooms?.map((roomType: any, idx: number) => (
              <Fragment key={idx}>
                {isRoomTypeV2 ? (
                  <RoomTypeCardV2
                    id={`room-${roomType?.id}`}
                    key={roomType?.id}
                    data={roomType}
                    isDesktop={props.isDesktop}
                    activeTab={props?.activeTab}
                    nextRoomId={
                      props.data.length > idx + 1 ? `room-${props.data[idx + 1]?.id}` : ""
                    }
                  />
                ) : (
                  <RoomTypeCard
                    id={`room-type-card-${idx}`}
                    key={roomType?.id}
                    data={roomType}
                    isDesktop={props.isDesktop}
                    activeTab={props?.activeTab}
                  />
                )}
              </Fragment>
            ))}
            {props.data.length != availableRooms?.length && (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                data-testid="sold-out-rooms-toggle"
                data-collapsed={props.data.length > rooms?.length ? "true" : "false"}
                className={formatClassNames(
                  styles.showMoreContainer,
                  props.isDesktop && styles.showMoreContainerDesktop,
                )}
                onClick={updateRoomCount}
              >
                <span
                  className={formatClassNames(styles.soldOutBadge, isDesktop && styles.desktop)}
                >
                  {t("propertypage.roomtypesectiondynamic.soldout")}
                </span>
                <Button
                  dataTestId={
                    props.data.length > rooms?.length
                      ? `${t("propertypage.roomtypesectiondynamic.viewsoldoutrooms")}`
                      : `${t("propertypage.roomtypesectiondynamic.hidesoldoutrooms")}`
                  }
                  variant="subtle"
                  className={formatClassNames(
                    styles.showMoreButton,
                    props.data.length === rooms?.length && styles.moreTenanciesButton,
                  )}
                  onClick={updateRoomCount}
                  RightIcon={() => <PaymentChevronDown width={12} height={12} />}
                >
                  <span className={styles.buttonText}>
                    {props.data.length > rooms?.length
                      ? `${t("propertypage.roomtypesectiondynamic.viewsoldoutrooms")} (${
                          soldOutRooms?.length
                        })`
                      : `${t("propertypage.roomtypesectiondynamic.hidesoldoutrooms")} (${
                          soldOutRooms?.length
                        })`}
                  </span>
                </Button>
              </div>
            )}
            {props.data.length === 0 && <NoRoomTypesFound />}
          </>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(CustomAccordionItem, {
  componentName: "CustomAccordionItem-RoomTypeSection-PropertyPage",
  showForChina: true,
});
